import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  TextInput, Select, MultiSelect, Radio, Button,
  FileInput, Image, Container, Card, Title, Loader
} from '@mantine/core';
import { makeGetRequest, makeMultipartFormPostRequest } from '../services/httpHelper';
import { IMAGE_BASE_URL } from '../services/paths';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import { isErrorObject } from '../utils/utils';
import ErrorHandler from '../services/ErrorHandler';
import { BlogCategory } from '../types';
import { useNavigate } from 'react-router-dom';

interface BlogPostFormProps {
  initialData?: any;
  onSubmitSuccess?: () => void;
}

const BlogPostForm: React.FC<BlogPostFormProps> = ({ initialData }) => {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(initialData?.featured_image ? `${IMAGE_BASE_URL}${initialData.featured_image}` : null);

  const navigate = useNavigate();


  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Superscript,
      SubScript,
      Highlight,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: initialData?.body || '',
  });

  const form = useForm({
    initialValues: {
      title: initialData?.title || '',
      category: initialData?.category || '',
      body: initialData?.body || '',
      tags: initialData?.tags || [],
      published: initialData?.published ? 'true' : 'false',
      featured_image: featuredImage,
    },
    validate: {
      title: (value) => (value.trim().length === 0 ? 'Title is required' : null),
      category: (value) => (!value ? 'Category is required' : null),
      body: (value) => (value.trim().length === 0 ? 'Body is required' : null),
      featured_image: (value) => (!initialData && !value ? 'Featured image is required' : null),
    },
  });

  useEffect(() => {
    const fetchCategories = async () => {
      const res = await makeGetRequest('blog-post-categories');
      if (isErrorObject(res)) {
        setError(ErrorHandler(res));
      } else {
        setCategories(res.data);
        setError(null);
      }
    };
    fetchCategories();
  }, []);

  // Update form body when editor content changes
  useEffect(() => {
    if (editor) {
      editor.on('update', () => {
        form.setFieldValue('body', editor.getHTML());
      });
    }
  }, [editor]);

  const handleImageChange = (file: File | null) => {
    if (file) {
      setFeaturedImage(file);
      setPreviewImage(URL.createObjectURL(file));
      form.setFieldValue('featured_image', file);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault(); // Prevent page refresh

    if (!editor?.getText().trim()) {
      alert('Enter post body contents');
      return;
    }

    const values = form.values;
    const formData = new FormData();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    formData.append('user_id',user.id)

    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof typeof values; // Ensure key is a valid property
      
      if (typedKey === 'tags' && Array.isArray(values[typedKey])) {
        formData.append(typedKey, values[typedKey].join(',')); // Convert array to string
      } else if (values[typedKey] instanceof File) {
        formData.append(typedKey, values[typedKey]); // Ensure file is handled properly
      } else {
        formData.append(typedKey, String(values[typedKey])); // Convert other values to string
      }
    });
    

    setIsSubmitting(true);

    const response = await makeMultipartFormPostRequest('blog-post', formData);

    if (isErrorObject(response)) {
      setError(ErrorHandler(response));
    } else {
      navigate(`/blog/post/${response.data.slug}/${response.data.id}`)
    }

    setIsSubmitting(false);
  };

  if(error){
    console.log(error)
  }

  return (
    <Container size="sm">
      <Card shadow="sm" padding="lg">
        <Title order={3}>{initialData ? 'Edit Post' : 'Create New Post'}</Title>
        {isSubmitting && <Loader />}
        <form onSubmit={handleSubmit}>
          <TextInput label="Title" placeholder="Enter post title" {...form.getInputProps('title')} required />

          <Select
            label="Category"
            placeholder="Select category"
            data={categories.map((cat) => ({ value: cat.id.toString(), label: cat.category }))}
            {...form.getInputProps('category')}
            required
          />

          <RichTextEditor editor={editor}>
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
                                                      <RichTextEditor.ControlsGroup>
                                                          <RichTextEditor.Bold/>
                                                          <RichTextEditor.Italic/>
                                                          <RichTextEditor.Underline/>
                                                          <RichTextEditor.Strikethrough/>
                                                          <RichTextEditor.ClearFormatting/>
                                                          <RichTextEditor.Highlight/>
                                                          <RichTextEditor.Code/>
                                                      </RichTextEditor.ControlsGroup>
                                                      <RichTextEditor.ControlsGroup>
                                                <RichTextEditor.AlignLeft />
                                                <RichTextEditor.AlignCenter />
                                                <RichTextEditor.AlignJustify />
                                                <RichTextEditor.AlignRight />
                                              </RichTextEditor.ControlsGroup>
          
                                                      <RichTextEditor.ControlsGroup>
                                                          <RichTextEditor.H1/>
                                                          <RichTextEditor.H2/>
                                                          <RichTextEditor.H3/>
                                                          <RichTextEditor.H4/>
                                                      </RichTextEditor.ControlsGroup>
          
                                                      <RichTextEditor.ControlsGroup>
                                                          <RichTextEditor.Blockquote/>
                                                          <RichTextEditor.Hr/>
                                                          <RichTextEditor.BulletList/>
                                                          <RichTextEditor.OrderedList/>
                                                          <RichTextEditor.Subscript/>
                                                          <RichTextEditor.Superscript/>
                                                      </RichTextEditor.ControlsGroup>
          
                                                      <RichTextEditor.ControlsGroup>
                                                          <RichTextEditor.Link/>
                                                          <RichTextEditor.Unlink/>
                                                      </RichTextEditor.ControlsGroup>
          
                                                      <RichTextEditor.ControlsGroup>
                                                        
                                                      </RichTextEditor.ControlsGroup>
                                                  </RichTextEditor.Toolbar>
                                          
                                                  <RichTextEditor.Content/>
                                              </RichTextEditor>

          {/* MultiSelect for Tags */}
          <MultiSelect
            label="Tags"
            placeholder="Enter tags"
            data={form.values.tags.map((tag:string) => ({ value: tag, label: tag }))}
            creatable
            getCreateLabel={(query) => `+ Add ${query}`}
            onCreate={(query) => {
              form.setFieldValue('tags', [...form.values.tags, query]);
              return query;
            }}
            {...form.getInputProps('tags')}
          />

          <Radio.Group label="Published" {...form.getInputProps('published')}>
            <Radio value="true" label="Yes" />
            <Radio value="false" label="No" />
          </Radio.Group>

          <FileInput label="Featured Image" placeholder="Upload image" onChange={handleImageChange} />
          {previewImage && <Image src={previewImage} height={200} mt="md" />}

          <Button type="submit" fullWidth mt="md">
            {isSubmitting ? 'Saving...' : initialData ? 'Update Post' : 'Create Post'}
          </Button>
        </form>
      </Card>
    </Container>
  );
};

export default BlogPostForm;
